import {
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type Ref,
} from "react";
import { Mail, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ChipGroup } from "@lytra/frontend-common/components/v2/ui/chip";
import { useFieldControl } from "@lytra/frontend-common/components/v2/ui/field";
import { Icon } from "@lytra/frontend-common/components/v2/ui/icon";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@lytra/frontend-common/components/v2/ui/popover";
import { FOCUS_RING_INSET } from "@lytra/frontend-common/components/v2/lib/focus";
import { cn } from "@lytra/frontend-common/components/v2/lib/utils";
import {
  filterColleagues,
  type Colleague,
  type PickerAgent,
} from "./MentionList";
import {
  ColleagueOptionContent,
  PICKER_ROW,
  PickerMessageRow,
  PickerSkeletonRows,
} from "./colleaguePickerRows";
import { RecipientChip } from "./RecipientChip";
import { useLiveAnnouncement } from "./useLiveAnnouncement";
import {
  MAX_FORWARD_RECIPIENTS,
  addRecipient,
  classifyRecipient,
  isAddressShaped,
  matchAgentByAddress,
  splitAddressInput,
  type AddRecipientResult,
  type AgentsState,
  type RecipientCandidate,
  type RecipientEntry,
} from "./recipients";

export interface RecipientFieldProps {
  value: RecipientEntry[];
  onChange: (next: RecipientEntry[]) => void;
  /** The tenant's colleagues (the `agents` query). */
  agents: readonly PickerAgent[];
  agentsState: AgentsState;
  /** The tenant's own mail domains; empty, null or not loaded means no chip warns. */
  internalDomains?: readonly string[] | null;
  viewerId?: string;
  viewerEmail?: string | null;
  /** The field-level error text; the parent renders it (v2 `Field` error), the field only marks itself invalid. */
  error?: string | null;
  onError: (message: string | null) => void;
  disabled?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  /** Id of the element that summarises the chips (the dialog's channel summary line). */
  describedById?: string;
  /**
   * False while sending by mail is off: only colleagues with a Lytra account can be added, a
   * typed address of anyone else is refused and the hint says why.
   */
  mailEnabled?: boolean;
}

type Option =
  | { kind: "colleague"; colleague: Colleague }
  | { kind: "address"; address: string };

const DUPLICATE_PULSE_MS = 700;

/**
 * The recipient field of "An Kollegen senden" (#717, UI-SPEC section 3): a token input, not a
 * Combobox (that one is a Select with a search box, no free text, no chips). Typed, pasted or
 * picked recipients become chips marked "in Lytra" or "per Mail"; a "per Mail" address outside
 * the tenant's domains warns inline (D-10). The colleague picker reuses the phase 2 filter
 * (D-11). The client side is a preview, the server decides the channel at send time (D-01).
 *
 * The field renders its own hint (no chips), the foreign-domain warning lines, the picker and
 * a polite status for flips and duplicates; the channel summary and the error line belong to
 * the dialog around it (`describedById`, `error`, `onError`).
 */
export function RecipientField({
  value,
  onChange,
  agents,
  agentsState,
  internalDomains,
  viewerId,
  viewerEmail,
  error,
  onError,
  disabled = false,
  inputRef,
  describedById,
  mailEnabled = true,
}: RecipientFieldProps) {
  const { t } = useTranslation();
  const listId = useId();
  const warningId = useId();
  const hintId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const fieldInputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(inputRef, () => fieldInputRef.current!);

  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState<number | null>(null);
  // Flips and duplicates are announced every time, also the same one twice.
  const { announce, region: statusRegion } = useLiveAnnouncement();
  const [pulseKey, setPulseKey] = useState<string | null>(null);
  const [previousAgentsState, setPreviousAgentsState] = useState(agentsState);

  const ctx = { viewerId, viewerEmail, agents, mailEnabled };
  const classify = (entry: RecipientEntry, state: AgentsState = agentsState) =>
    classifyRecipient(entry, agents, state, internalDomains);

  // A typed address the list could not judge yet flips to "in Lytra" once it answers: say so,
  // never by moving focus. Adjusting state while rendering is the supported way to react to a
  // prop change without an effect.
  if (previousAgentsState !== agentsState) {
    setPreviousAgentsState(agentsState);
    if (previousAgentsState === "loading" && agentsState === "ready") {
      const flipped = value
        .filter((entry) => !entry.userId)
        .map((entry) => classify(entry, "ready"))
        .filter((classified) => classified.channel === "lytra");
      if (flipped.length > 0) {
        announce(
          flipped
            .map((classified) =>
              t("tickets.forward.status.isLytra", { name: classified.label }),
            )
            .join(" "),
        );
      }
    }
  }

  useEffect(() => {
    if (pulseKey === null) return;
    const timer = setTimeout(() => setPulseKey(null), DUPLICATE_PULSE_MS);
    return () => clearTimeout(timer);
  }, [pulseKey]);

  const query = text.trim();
  const shaped = isAddressShaped(query);
  const atCap = value.length >= MAX_FORWARD_RECIPIENTS;

  // Everyone already in the list, by account, whether they were picked or typed by address.
  const addedIds = new Set(
    value.flatMap((entry) => {
      const id =
        entry.userId ?? matchAgentByAddress(agents, entry.address)?.id ?? null;
      return id ? [id] : [];
    }),
  );
  const colleagues =
    agentsState === "ready"
      ? filterColleagues(
          agents.filter((agent) => !addedIds.has(agent.id)),
          query,
          viewerId,
        )
      : [];
  const freeAddress =
    mailEnabled && shaped && !matchAgentByAddress(agents, query);
  const options: Option[] = atCap
    ? []
    : [
        ...colleagues.map(
          (colleague): Option => ({ kind: "colleague", colleague }),
        ),
        ...(freeAddress
          ? [{ kind: "address", address: query } satisfies Option]
          : []),
      ];

  // A name typed with colleagues to pick: the first one is the answer to Enter. An address
  // (or nothing typed yet) highlights nothing until an arrow key does.
  const defaultHighlight =
    query !== "" && !shaped && options[0]?.kind === "colleague" ? 0 : -1;
  const active =
    highlight !== null && highlight < options.length
      ? highlight
      : defaultHighlight;
  const optionId = (index: number) => `${listId}-option-${index}`;
  const panelOpen = open && !disabled;

  const errorMessage = (
    result: NonNullable<AddRecipientResult["error"]>,
    piece: string,
  ) =>
    result === "self"
      ? t("tickets.forward.errors.self")
      : result === "cap"
        ? t("tickets.forward.errors.cap")
        : result === "noAccount"
          ? t("tickets.forward.errors.noAccount", { input: piece })
          : t("tickets.forward.errors.invalidAddress", { input: piece });

  /**
   * Commit candidates one after the other. Refused pieces are handed back so the typed text
   * stays; the error names the first one. Duplicates add nothing and are announced.
   */
  function commit(candidates: RecipientCandidate[]): string[] {
    let list = value;
    const leftover: string[] = [];
    let firstError: string | null = null;
    for (const candidate of candidates) {
      const result = addRecipient(list, candidate, ctx);
      const piece = candidate.address ?? candidate.label ?? "";
      if (result.error) {
        leftover.push(piece);
        firstError ??= errorMessage(result.error, piece);
      } else if (result.duplicateOf) {
        const label = classify(result.duplicateOf).label;
        announce(t("tickets.forward.status.duplicate", { label }));
        setPulseKey(result.duplicateOf.key);
      } else {
        list = result.list;
        const added = list[list.length - 1];
        const classified = classify(added);
        if (classified.channel === "lytra" && !added.userId) {
          announce(
            t("tickets.forward.status.isLytra", { name: classified.label }),
          );
        }
      }
    }
    if (list !== value) onChange(list);
    onError(firstError);
    return leftover;
  }

  function commitText(remainder = "") {
    const pieces = splitAddressInput(text);
    const leftover = commit(pieces.map((address) => ({ address })));
    setText([...leftover, remainder].filter(Boolean).join(", "));
    setHighlight(null);
  }

  function select(option: Option) {
    if (option.kind === "colleague") {
      const { colleague } = option;
      commit([
        {
          userId: colleague.id,
          address: colleague.email,
          label: colleague.label,
        },
      ]);
    } else {
      commit([{ address: option.address }]);
    }
    setText("");
    setHighlight(null);
    setOpen(false);
  }

  function onInputChange(next: string) {
    if (error) onError(null);
    if (/[,;\n]/.test(next)) {
      // A comma or semicolon commits what stands before it; text after the last one stays typed.
      const segments = next.split(/[,;\n]/);
      const remainder = segments.pop()?.trim() ?? "";
      const leftover = commit(
        segments
          .map((segment) => segment.trim())
          .filter(Boolean)
          .map((address) => ({ address })),
      );
      setText([...leftover, remainder].filter(Boolean).join(", "));
      setHighlight(null);
      return;
    }
    setText(next);
    setHighlight(null);
    setOpen(true);
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData("text");
    // One plain token (a name part, one address) goes into the input as usual.
    if (!/[\s,;]/.test(pasted.trim())) return;
    event.preventDefault();
    if (error) onError(null);
    const leftover = commit(
      splitAddressInput(pasted).map((address) => ({ address })),
    );
    setText([text.trim(), ...leftover].filter(Boolean).join(", "));
    setHighlight(null);
  }

  function move(step: 1 | -1) {
    setOpen(true);
    if (options.length === 0) return;
    if (step === 1) setHighlight(active < 0 ? 0 : (active + 1) % options.length);
    else setHighlight(active <= 0 ? options.length - 1 : active - 1);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        move(-1);
        break;
      case "Enter":
        if (panelOpen && active >= 0 && options[active]) {
          event.preventDefault();
          select(options[active]);
        } else if (text.trim() !== "") {
          event.preventDefault();
          commitText();
        }
        break;
      case "Escape":
        // The first Escape closes the picker and keeps the text; the next one is the dialog's.
        if (panelOpen) {
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
        }
        break;
      case "Backspace":
        if (text === "" && value.length > 0) {
          event.preventDefault();
          const removers =
            wrapperRef.current?.querySelectorAll<HTMLElement>(
              '[data-slot="chip"] button',
            ) ?? [];
          removers[removers.length - 1]?.focus();
        }
        break;
      default:
    }
  }

  const foreignEntries = value.filter((entry) => classify(entry).foreign);
  const describedBy =
    [
      describedById,
      foreignEntries.length > 0 ? warningId : null,
      value.length === 0 && !error ? hintId : null,
    ]
      .filter(Boolean)
      .join(" ") || undefined;
  const control = useFieldControl({
    disabled,
    "aria-describedby": describedBy,
    "aria-invalid": error ? true : undefined,
  });
  const inputDisabled = control.disabled ?? false;

  return (
    <div data-slot="recipient-field" className="flex flex-col gap-1">
      <Popover
        open={panelOpen}
        onOpenChange={(next) => {
          if (!next) setOpen(false);
        }}
      >
        <PopoverAnchor asChild>
          <div
            ref={wrapperRef}
            className={cn(
              "flex min-h-8 flex-wrap items-center gap-1 rounded-md border bg-background px-2 py-1 outline-ring motion-reduce:transition-none",
              "focus-within:outline-2 focus-within:outline-solid focus-within:-outline-offset-1",
              error ? "border-destructive outline-destructive" : "border-input hover:border-ink-muted",
              inputDisabled && "opacity-50",
            )}
            onMouseDown={(event) => {
              // A click on the empty part of the field is a click into the input.
              if (event.target === event.currentTarget) {
                event.preventDefault();
                fieldInputRef.current?.focus();
              }
            }}
          >
            <ChipGroup
              className="contents"
              aria-label={t("tickets.forward.recipients.label")}
            >
              {value.map((entry) => {
                const classified = classify(entry);
                return (
                  <RecipientChip
                    key={entry.key}
                    label={classified.label}
                    channel={classified.channel}
                    foreign={classified.foreign}
                    pulse={pulseKey === entry.key}
                    onRemove={
                      inputDisabled
                        ? undefined
                        : () => {
                            onChange(value.filter((e) => e.key !== entry.key));
                            if (value.length === 1) fieldInputRef.current?.focus();
                            if (error) onError(null);
                          }
                    }
                  />
                );
              })}
            </ChipGroup>
            <input
              {...control}
              ref={fieldInputRef}
              type="text"
              role="combobox"
              aria-label={t("tickets.forward.recipients.inputName")}
              aria-haspopup="listbox"
              aria-autocomplete="list"
              aria-expanded={panelOpen}
              aria-controls={listId}
              aria-activedescendant={
                panelOpen && active >= 0 ? optionId(active) : undefined
              }
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              value={text}
              disabled={inputDisabled}
              className="min-w-40 flex-1 bg-transparent font-sans text-body text-ink outline-hidden"
              onChange={(event) => onInputChange(event.target.value)}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              onClick={() => {
                if (text === "") setOpen(true);
              }}
              onBlur={() => {
                setOpen(false);
                if (text.trim() !== "") commitText();
              }}
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          // A combobox popup is not a dialog: the listbox inside carries the semantics.
          role="presentation"
          align="start"
          padding="none"
          width="trigger"
          className="min-w-60 p-1"
          style={{
            maxHeight:
              "min(14rem, var(--radix-popover-content-available-height))",
          }}
          // Focus stays in the input; the panel never takes it.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            if (
              event.target instanceof Node &&
              wrapperRef.current?.contains(event.target)
            ) {
              event.preventDefault();
            }
          }}
          onMouseDown={(event) => event.preventDefault()}
        >
          <div
            id={listId}
            role="listbox"
            aria-label={t("tickets.forward.picker.name")}
            aria-busy={agentsState === "loading" || undefined}
            className="flex flex-col"
          >
            {atCap && (
              <PickerMessageRow id={optionId(0)}>
                {t("tickets.forward.picker.cap")}
              </PickerMessageRow>
            )}
            {!atCap && agentsState === "loading" && <PickerSkeletonRows />}
            {!atCap && agentsState === "error" && (
              <PickerMessageRow id={`${listId}-message`}>
                {t(
                  mailEnabled
                    ? "tickets.forward.picker.error"
                    : "tickets.forward.picker.errorLytraOnly",
                )}
              </PickerMessageRow>
            )}
            {!atCap && agentsState === "ready" && options.length === 0 && (
              <PickerMessageRow id={`${listId}-message`}>
                {t(
                  mailEnabled
                    ? "tickets.forward.picker.empty"
                    : "tickets.forward.picker.emptyLytraOnly",
                )}
              </PickerMessageRow>
            )}
            {options.map((option, index) => {
              const highlighted = index === active;
              return (
                <div
                  key={
                    option.kind === "colleague"
                      ? option.colleague.id
                      : "free-address"
                  }
                  id={optionId(index)}
                  role="option"
                  aria-selected={highlighted}
                  data-focus={highlighted ? "" : undefined}
                  className={cn(
                    PICKER_ROW,
                    FOCUS_RING_INSET,
                    highlighted && "bg-hover-overlay",
                  )}
                  onMouseMove={() => {
                    if (!highlighted) setHighlight(index);
                  }}
                  onClick={() => select(option)}
                >
                  {option.kind === "colleague" ? (
                    <ColleagueOptionContent colleague={option.colleague} />
                  ) : (
                    <>
                      <span className="flex size-5 shrink-0 items-center justify-center text-ink-muted">
                        <Icon icon={Mail} size={12} />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-body text-ink">
                        {t("tickets.forward.picker.addAddress", {
                          address: option.address,
                        })}
                      </span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {agentsState === "loading" && !atCap && (
            <span role="status" className="sr-only">
              {t("tickets.forward.picker.loading")}
            </span>
          )}
        </PopoverContent>
      </Popover>

      {foreignEntries.length > 0 && (
        <div id={warningId} className="flex flex-col gap-0.5">
          {foreignEntries.slice(0, 3).map((entry) => (
            <p
              key={entry.key}
              className="flex items-start gap-1 text-caption text-warning-strong"
            >
              <span className="mt-0.5 flex shrink-0">
                <Icon icon={TriangleAlert} size={12} />
              </span>
              <span className="min-w-0 break-words">
                {t("tickets.forward.warning.foreignLine", {
                  address: entry.address ?? entry.label,
                })}
              </span>
            </p>
          ))}
          {foreignEntries.length > 3 && (
            <p className="text-caption text-warning-strong">
              {t("tickets.forward.warning.more", {
                count: foreignEntries.length - 3,
              })}
            </p>
          )}
        </div>
      )}

      {value.length === 0 && !error && (
        <p id={hintId} className="text-caption text-ink-muted">
          {t(
            mailEnabled
              ? "tickets.forward.recipients.hint"
              : "tickets.forward.recipients.hintLytraOnly",
          )}
        </p>
      )}

      {statusRegion}
    </div>
  );
}
